import type { Dispatch, SetStateAction } from "react";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@stylingcv/utils";
import type { InputProps } from "./input";
import { Input } from "./input";

type BadgeInputProps = Omit<InputProps, "value" | "onChange"> & {
  value: string[];
  onChange: (value: string[]) => void;
  setPendingKeyword?: Dispatch<SetStateAction<string>>;
  suggestions?: string[];
};

export const BadgeInput = forwardRef<HTMLInputElement, BadgeInputProps>(
  ({ value, onChange, setPendingKeyword, suggestions, ...props }, ref) => {
    const [label, setLabel] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const processInput = useCallback(() => {
      const newLabels = label
        .split(",")
        .map((str) => str.trim())
        .filter(Boolean)
        .filter((str) => !value.includes(str));
      onChange([...new Set([...value, ...newLabels])]);
      setLabel("");
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }, [label, value, onChange]);

    const filteredSuggestions = useMemo(() => {
      if (!suggestions || !label.trim() || label.length < 2) return [];
      const query = label.toLowerCase().trim();
      return suggestions
        .filter(
          (s) => s.toLowerCase().includes(query) && !value.includes(s),
        )
        .slice(0, 8);
    }, [suggestions, label, value]);

    const selectSuggestion = useCallback(
      (suggestion: string) => {
        if (!value.includes(suggestion)) {
          onChange([...new Set([...value, suggestion])]);
        }
        setLabel("");
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      },
      [value, onChange],
    );

    useEffect(() => {
      if (label.includes(",")) {
        processInput();
      }
    }, [label, processInput]);

    useEffect(() => {
      if (setPendingKeyword) setPendingKeyword(label);
    }, [label, setPendingKeyword]);

    useEffect(() => {
      setShowSuggestions(filteredSuggestions.length > 0);
      setHighlightedIndex(-1);
    }, [filteredSuggestions.length]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          setShowSuggestions(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSuggestions && filteredSuggestions.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : 0,
          );
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredSuggestions.length - 1,
          );
          return;
        }
        if (event.key === "Enter" && highlightedIndex >= 0) {
          event.preventDefault();
          event.stopPropagation();
          selectSuggestion(filteredSuggestions[highlightedIndex]);
          return;
        }
        if (event.key === "Escape") {
          setShowSuggestions(false);
          setHighlightedIndex(-1);
          return;
        }
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        processInput();
      }
    };

    return (
      <div ref={wrapperRef} className="relative">
        <Input
          {...props}
          ref={ref}
          value={label}
          onKeyDown={onKeyDown}
          autoComplete="off"
          onChange={(event) => {
            setLabel(event.target.value);
          }}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            role="listbox"
            className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
          >
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm transition-colors",
                  index === highlightedIndex
                    ? "bg-primary/10 text-primary"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(suggestion);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);
